/**
 * Koras Kommentar – Portrait und ein Satz in einer weißen Pille.
 *
 * Genau ein Satz je Ansicht, aus den Daten abgeleitet, oder nichts. Hat der
 * Server nichts zu sagen, wird hier auch nichts gerendert: keine leere Pille,
 * kein Platzhalter, kein Füllsatz.
 *
 * Die Datei hält zwei Teile getrennt: den Abruf (`useKoraSatz`) und die
 * Darstellung (`KoraPille`). Der Abruf hat zwei Verwender – die Ecke unten
 * rechts und nichts sonst –, die Pille drei: die Ecke, die Vorschau im Admin
 * Center und künftig alles, was Kora zitieren will. Zusammengeschweißt wäre
 * die Vorschau gezwungen, sich einen Satz vom Server zu holen, den sie gar
 * nicht meint.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '../api';
import type { KoraSatz } from '../typen';
import { IconHaken } from './Icons';
import './korakommentar.css';

/**
 * Auf welchen Masken Kora spricht. Derselbe Schlüssel wie am Server – er
 * entscheidet dort, welche Regeln überhaupt geprüft werden.
 */
export type KoraMaske =
  | 'mein-tag'
  | 'angebote'
  | 'auftraege'
  | 'auftragsauswertung'
  | 'finanzen';

/**
 * Koras Satz zu einer Maske. `null`, wenn sie nichts zu sagen hat.
 *
 * Ohne Maske wird gar nicht gefragt: Auf Kundenstamm oder Buchhaltung
 * schweigt sie ohnehin, und ein Abruf, dessen Antwort niemand ansieht, ist
 * eine Anfrage zu viel bei jedem Seitenwechsel.
 */
export function useKoraSatz(maske: KoraMaske | null) {
  return useQuery({
    queryKey: ['kora-satz', maske],
    queryFn: () => api.hole<KoraSatz | null>(`/kora/satz?maske=${maske}`),
    enabled: maske !== null,
    /*
     * Der Satz gilt für die Sitzung, nicht für den Augenblick: Wer zwischen
     * zwei Masken wechselt, soll nicht jedes Mal neu lesen müssen. Deshalb
     * kein automatisches Nachladen beim Fensterwechsel.
     */
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    /* Fällt Kora aus, bleibt die Maske vollständig – sie schweigt dann eben. */
    retry: false,
  });
}

/**
 * Die Pille selbst, ohne Abfrage.
 *
 * `onWeg` blendet den Haken ein, mit dem man den Hinweis quittiert. Wo Kora
 * nicht im Weg steht – in der Vorschau des Admin Centers – bleibt er weg;
 * dort gäbe es auch nichts zu quittieren.
 */
export function KoraPille({
  satz,
  klein,
  onWeg,
}: {
  satz: KoraSatz;
  klein?: boolean;
  onWeg?: () => void;
}) {
  return (
    <div className={`kora-pille ${klein ? 'klein' : ''}`}>
      {/*
        Der Kreis schneidet, das Bild zoomt. Beides am selben Element ginge
        nicht: Eine Skalierung vergrößert den Rahmen mit, und aus dem Kreis
        würde eine Scheibe von 96 Pixeln.
      */}
      <span className="kora-portrait">
        <img src={satz.bild} alt={`Kora, Stimmung ${satz.pose}`} />
      </span>
      <div className="kora-text">
        {/*
          Kursiv ist Koras Stimme im ganzen System – sonst ist nichts in der
          Oberfläche kursiv. Anführungszeichen braucht es deshalb nicht.
        */}
        <p className="kora-satz">{satz.text}</p>
        <div className="kora-fuss">KORA · {satz.pose}</div>
      </div>
      {onWeg && (
        <button type="button" className="kora-weg" onClick={onWeg} title="Verstanden">
          {/*
            Ein Haken, kein Kreuz: Man wischt Kora nicht weg, man nimmt ihren
            Hinweis zur Kenntnis. Das Kreuz hieß „schließen" und klang, als
            wäre der Satz eine Störung.
          */}
          <IconHaken groesse={15} />
          <span className="eh-nur-vorlesen">Verstanden – Hinweis ausblenden</span>
        </button>
      )}
    </div>
  );
}
